import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { Chart } from '../Chart/ChartRoot/Chart';
import { useConditionalChart } from '../ConditionalChartProvider/ConditionalChartProvider';
import { ResizeHandle } from '../ContentGrid/ResizeHandle';
import { DEFAULT_GRID_ROWS } from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../PageConfigProvider/types/item';

const STORAGE_KEY_PREFIX = 'custom-dash-split-';

const getStorageKey = (pageId: string) => `${STORAGE_KEY_PREFIX}${pageId}`;

const loadSavedSizes = (pageId: string) => {
  const saved = localStorage.getItem(getStorageKey(pageId));
  if (!saved) return null;

  const parsed = JSON.parse(saved);
  return parsed;
};

const saveSizes = (
  pageId: string,
  leftWidth: number,
  isLeftCollapsed: boolean,
  isRightCollapsed: boolean
) => {
  localStorage.setItem(
    getStorageKey(pageId),
    JSON.stringify({
      leftWidth,
      isLeftCollapsed,
      isRightCollapsed,
    })
  );
};

type PositionedItemWithData = PositionedItem & {
  data?: object;
};

export const ResizableSplitPane = ({ items }: { items: PositionedItem[] }) => {
  const { condiationalCharts } = useConditionalChart();
  const leftItem = items?.[0];
  const rightItem = items?.[1];

  const additionalItems = items.slice(2) ?? [];
  const filteredConditionalItems: PositionedItemWithData[] = additionalItems
    .filter((item) => {
      const chartCondition = condiationalCharts.find(
        (chart) => chart.chartId === item.id
      );
      if (!chartCondition) return true;
      return chartCondition.isVisible;
    })
    .map((item) => {
      const chartCondition = condiationalCharts.find(
        (chart) => chart.chartId === item.id
      );
      return {
        ...item,
        data: chartCondition?.chartData ?? undefined,
      };
    });

  const { config } = useTabContentConfig();
  const pageId = config.id;

  const effectiveFilters = useEffectiveFilters();
  const hasVisibleFilters = effectiveFilters.some((filter) => filter.isVisible);

  const saved = loadSavedSizes(pageId);

  const [leftWidth, setLeftWidth] = useState(saved?.leftWidth ?? 50);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(
    saved?.isLeftCollapsed ?? false
  );
  const [isRightCollapsed, setIsRightCollapsed] = useState(
    saved?.isRightCollapsed ?? false
  );

  useEffect(() => {
    saveSizes(pageId, leftWidth, isLeftCollapsed, isRightCollapsed);
  }, [leftWidth, isLeftCollapsed, isRightCollapsed, pageId]);

  const handleResize = useCallback((newLeftWidth: number) => {
    const threshold = 5;

    if (newLeftWidth < threshold) {
      setIsLeftCollapsed(true);
      setIsRightCollapsed(false);
      setLeftWidth(50);
    } else if (newLeftWidth > 100 - threshold) {
      setIsRightCollapsed(true);
      setIsLeftCollapsed(false);
      setLeftWidth(50);
    } else {
      setLeftWidth(newLeftWidth);
      setIsLeftCollapsed(false);
      setIsRightCollapsed(false);
    }
  }, []);

  const handleCollapseLeft = useCallback(() => {
    if (isLeftCollapsed) {
      setIsLeftCollapsed(false);
      setIsRightCollapsed(false);
      setLeftWidth(50);
    } else {
      setIsLeftCollapsed(true);
      setIsRightCollapsed(false);
    }
  }, [isLeftCollapsed]);

  const handleCollapseRight = useCallback(() => {
    if (isRightCollapsed) {
      setIsLeftCollapsed(false);
      setIsRightCollapsed(false);
      setLeftWidth(50);
    } else {
      setIsRightCollapsed(true);
      setIsLeftCollapsed(false);
    }
  }, [isRightCollapsed]);

  const gridTemplateColumns = (() => {
    if (isLeftCollapsed) {
      return '0fr 8px 1fr';
    }
    if (isRightCollapsed) {
      return '1fr 8px 0fr';
    }
    const rightWidth = 100 - leftWidth;
    return `${leftWidth}fr 8px ${rightWidth}fr`;
  })();

  if (!hasVisibleFilters) {
    return (
      <Box width="100%" height="100%">
        {rightItem && <Chart item={rightItem} />}
      </Box>
    );
  }

  const rightItemRow = rightItem?.position?.y ? rightItem.position.y + 1 : 1;
  const rightItemRowSpan = rightItem?.position?.height
    ? rightItem.position.height
    : DEFAULT_GRID_ROWS;
  const leftItemRow = leftItem?.position?.y ? leftItem.position.y + 1 : 1;
  const leftItemRowSpan = leftItem?.position?.height
    ? leftItem.position.height
    : DEFAULT_GRID_ROWS;

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      display="grid"
      gridTemplateRows={`repeat(${DEFAULT_GRID_ROWS}, 1fr)`}
      gridTemplateColumns={gridTemplateColumns}
      gap="0.25rem"
      transition={
        isLeftCollapsed || isRightCollapsed
          ? 'grid-template-columns 0.2s ease'
          : 'none'
      }
    >
      <Box
        height="100%"
        minWidth="0"
        minHeight="0"
        overflow="hidden"
        gridColumnStart={1}
        gridColumnEnd={2}
        gridRow={`${leftItemRow} / span ${leftItemRowSpan}`}
      >
        {!isLeftCollapsed && leftItem && <Chart item={leftItem} />}
      </Box>

      <ResizeHandle
        onResize={handleResize}
        initialLeftWidth={leftWidth}
        onCollapseLeft={handleCollapseLeft}
        onCollapseRight={handleCollapseRight}
        isLeftCollapsed={isLeftCollapsed}
        isRightCollapsed={isRightCollapsed}
      />

      <Box
        height="100%"
        minWidth="0"
        overflow="hidden"
        minHeight="0"
        position="relative"
        gridColumnStart={3}
        gridColumnEnd={4}
        gridRow={`${rightItemRow} / span ${rightItemRowSpan}`}
      >
        {!isRightCollapsed && rightItem && <Chart item={rightItem} />}
      </Box>
      {filteredConditionalItems.map((item) => (
        <Box
          key={item.id}
          position="relative"
          minWidth="0"
          minHeight="0"
          gridColumnStart={3}
          gridColumnEnd={4}
          gridRow={`${item.position.y + 1} / span ${item.position.height}`}
        >
          <Chart item={item} />
        </Box>
      ))}
    </Box>
  );
};
